import React, { memo, useEffect, useMemo, useState } from 'react';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import ReactMarkdown from 'react-markdown';
import { Button, Divider, Image, Input, message, Steps, Tooltip } from 'antd';
import { makeSectionsFromLinesByHeaderRegex } from 'components/exception/issue/RegisterInfo/RegisterInfo';
import { CS_STYLES } from 'utils/constants/style-constants';
import { TbTools } from 'react-icons/tb';
import { MdOutlineGpsFixed } from 'react-icons/md';
import { green } from '@ant-design/colors';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import DeepAnalysisIcon from 'svg/v2/newcs_aiassistant_deepanalysis_icon.svg';
import { isZh, ze } from 'utils/zhEn';
import { ErrorBoundary } from 'react-error-boundary';
import RootCauseIcon from 'svg/v2/newcs_aiassistant_rootcauseanalysis_icon.svg';
import SuggestionIcon from 'svg/v2/newcs_aiassistant_repairsuggestions_icon.svg';
import CsSimpleButton from 'components/antd-extension/CsSimpleButton';
import { FaQuoteRight } from "react-icons/fa";
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';
import { LinkOutlined } from '@ant-design/icons';
import { isNotNullish } from 'utils/nullish';
import { AiIssueAnalysisUtil } from 'components/top/AiDrawer/ai-issue-analysis';
import scss from './AiDrawer.scss';
import AiIssueAnalysisJsonAnswerRenderer from 'components/top/AiDrawer/AiIssueAnalysisJsonAnswerRenderer';

// pre-wrap 允许在空格和 <wbr> 处换行。normal 表示不从单词中间拆开。
const codeWrapStyle = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  maxWidth: '100%',
};

/** 片段之间插入 <wbr>。断点规则见 splitCodeForSoftWrap。 */
function withCodeSoftWraps(children) {
  return React.Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string') {
      return child;
    }
    const parts = AiIssueAnalysisUtil.splitCodeForSoftWrap(child);
    return parts.map((part, index) => (
      <React.Fragment key={`${childIndex}-${index}`}>
        {index > 0 && <wbr />}
        {part}
      </React.Fragment>
    ));
  });
}

const markdownComponents = {
  img({ node, ...props}) {
    return <div style={{ maxWidth:'100%' }}>
      <Image src={props.src} />
    </div>
  },
  th: ({ node, ...props }) => (
    <th style={{ border: '1px solid #aaa', padding: '8px', textAlign: 'left' }} {...props}>
      {props.children}
    </th>
  ),
  td: ({ node, ...props }) => (
    <td style={{ border: '1px solid #aaa', padding: '8px' }} {...props}>
      {props.children}
    </td>
  ),
  code: ({ node, inline, children, ...props }) => (
    <code
      {...props}
      style={{ ...(props.style || {}), ...codeWrapStyle, display: inline ? undefined : 'block' }}
    >
      {withCodeSoftWraps(children)}
    </code>
  ),
  pre: ({ node, ...props }) => (
    <pre
      {...props}
      style={{
        ...(props.style || {}),
        maxWidth: '100%',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
      }}
      onScroll={(e) => e.stopPropagation()}
    />
  ),
};

// 配置rehype-sanitize的安全白名单
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'quoted-log-btn'],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes['*'] || []), 'className', 'style']
  },
};

// Step1和Step2，一条假设部分的渲染
const GuessBlock = ({
  text,
  quotedLogs,
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
  contextualMarkdownComponents,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isShowDeepAnalysisInput, setIsShowDeepAnalysisInput] = useState(false);
  const [inputValue, setInputValue] = useState('');

  text = (text || '').trim();
  if (text.startsWith('假设') || text.startsWith('Hypothesis')) {
    const splitRes = text.split('\n');
    const [guessTitle, ...proofLines] = splitRes;
    const enrichedProofLines = AiIssueAnalysisUtil.enrichQuotedLogLines(proofLines, (line) => {
      return quotedLogs?.findIndex(({ log }) => log && line.includes(log)) ?? -1;
    });

    const joinedProofLine = enrichedProofLines.join('\n');

    const proofLinesDom = <ReactMarkdown
      remarkPlugins={[gfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      linkTarget='_blank'
      children={joinedProofLine}
      components={contextualMarkdownComponents}
    />;


    return <div style={{ marginBottom: '8px' }}>
      <div
        style={{ backgroundColor: '#eceff5', padding: '4px 8px', borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
      >
        <div>{ guessTitle }</div>
        <div style={{ flexGrow: 1 }}/>
        { enableDeepAnalysis && <div
          style={{ display: 'flex', alignItems: 'center',  cursor: 'pointer' }}
          onClick={() => setIsShowDeepAnalysisInput(!isShowDeepAnalysisInput)}
        >
          <Tooltip title={ze('基于此深度分析', 'Deep Analysis')}>
          <DeepAnalysisIcon style={{ color: CS_STYLES.getPrimaryColor() }}/>
          </Tooltip>
        </div> }
        <div style={{ transform: 'scale(1.25)', display: 'flex', alignItems: 'center'}}><CsCollapseButton
          isSmallSize={true}
          isCollapsed={collapsed}
          onClick={() => setCollapsed(!collapsed)}
        /></div>
      </div>
      { !collapsed && proofLinesDom }
      { enableDeepAnalysis && isShowDeepAnalysisInput && <>
        <div><Input.TextArea
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder={ze('补充相关思路和依据信息', 'Input your relevant ideas and proofs.')}
        /></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '16px' }}>
          <Button
            type='primary'
            size='small'
            onClick={() => {
              const v = inputValue.trim();
              const queryText = v
                ? ze(`针对 ${guessTitle} 进行深入分析，并补充相关思路和依据信息如下：\n${v}`, `Conduct in-depth analysis on ${guessTitle} with additional relevant ideas and evidence as follows:\n${v}`)
                : ze(`针对 ${guessTitle} 进行深入分析`, `Conduct in-depth analysis on ${guessTitle}`);
              setInputValue('');
              setIsShowDeepAnalysisInput(false);
              onClickDeepAnalysis(queryText);
            }}
          >{ ze('补充分析', 'Submit') }</Button>
        </div>
      </> }
    </div>;
  } else {
    return <div>{ text }</div>;
  }
};

// Step1和Step2，手动补充假设部分的渲染
const AddNewGuessBlock = ({
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
}) => {
  const [isShowDeepAnalysisInput, setIsShowDeepAnalysisInput] = useState(false);
  const [inputValue, setInputValue] = useState('');

  return <div style={{ marginBottom: '8px' }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ flexGrow: 1 }} />
      <span style={{ color: CS_STYLES.SUB_TEXT_COLOR, fontSize: '12px', marginRight: isZh() ? '0' : '4px' }}>{ ze('如果未包含你认为的假设，点击这里', 'If not contains your hypothesis, click here to') }</span>
      <Button
        type='link'
        style={{ padding: '0', fontSize: '12px' }}
        onClick={() => setIsShowDeepAnalysisInput(!isShowDeepAnalysisInput)}
      >{ ze('补充假设', 'add hypothesis') }</Button>
    </div>
    { enableDeepAnalysis && isShowDeepAnalysisInput && <>
      <div><Input.TextArea
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder={ze('补充你认为的假设和依据信息，并基于此深度分析', 'Add your hypothesis and evidence, then conduct deep analysis based on it')}
      /></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '16px' }}>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            const v = inputValue.trim();
            if (!v) {
              message.warn(ze('未输入假设信息', 'Please input hypothesis information'));
              return;
            }
            const queryText = ze(`请针对以下新增假设，进行深入分析：\n${v}`, `Please conduct in-depth analysis on the following new hypothesis:\n${v}`);
            setInputValue('');
            setIsShowDeepAnalysisInput(false);
            onClickDeepAnalysis(queryText);
          }}
        >{ ze('补充分析', 'Submit') }</Button>
      </div>
    </> }
  </div>;
};

const AnswerSectionRenderer = ({
  section,
  otherInfo,
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
  contextualMarkdownComponents,
}) => {
  const { header, bodyLines } = section;

  if (!header) {
    return <ReactMarkdown
      remarkPlugins={[gfm]}
      linkTarget='_blank'
      children={bodyLines.join('\n')}
      components={markdownComponents}
    />;
  }

  const quotedLogs = useMemo(() => {
    return otherInfo?.quotedLogs || [];
  }, [otherInfo]);

  const {
    stepNumber,
    stepIcon,
    title,
  } = useMemo(() => {
    let stepNumber;
    let stepIcon;
    let title;

    // 匹配Step 1-3
    const match = header.match(/^#*\s*Step\s*(\d+):\s*(.*)$/);
    if (match) {
      stepNumber = Number(match[1]);
      title = match[2];
    } else if (header.includes(ze('修复建议', 'Fix Recommendations'))) {
      stepNumber = 5;
      title = ze('修复建议', 'Fix Recommendations');
      stepIcon = <SuggestionIcon width={32} height={32}  />;
    }

    if (stepNumber === 4) {
      title = ze('根因结论', 'Conclusion');
      stepIcon = <RootCauseIcon width={32} height={32} />;
    }

    return { stepNumber, stepIcon, title };
  }, [header]);

  const {
    rootCauseText,
    markdownText,
    customElementBlock,
  } = useMemo(() => {
    let rootCauseText = '';
    let markdownText = '';
    let customElementBlock = null;

    // 生成初始假设集 / 评估假设置信度
    if (stepNumber === 1 || stepNumber === 2) {
      let resultLines = [];
      if (true) {
        // 用两个换行连接，不然markdown不认为是换行
        const singleJoinedBodyLine = bodyLines.join('\n\n');
        // 将singleJoinedBodyLine用“假设”或者"Top"（必须在一行开头）分隔成string数组。
        const splitByGuess = singleJoinedBodyLine.split(/^(假设|Hypothesis |Top)/m);
        resultLines = [splitByGuess[0]];
        for (let i = 1; i < splitByGuess.length; i += 2) {
          resultLines.push(splitByGuess[i] + splitByGuess[i + 1]);
        }
      } else {
        resultLines = bodyLines;
      }
      const lineBlockList = resultLines.map(line => {
        line = (line || '').trim();
        if (line.startsWith('假设') || line.startsWith('Hypothesis')) {
          return <GuessBlock
            text={line}
            quotedLogs={quotedLogs}
            enableDeepAnalysis={enableDeepAnalysis && stepNumber === 1}
            onClickDeepAnalysis={onClickDeepAnalysis}
            contextualMarkdownComponents={contextualMarkdownComponents}
          />
        } else {
          return <ReactMarkdown
            remarkPlugins={[gfm]}
            linkTarget='_blank'
            children={line}
            components={markdownComponents}
          />;
        }
      });

      customElementBlock = <div>
        { lineBlockList }
        { stepNumber === 1 && enableDeepAnalysis && <AddNewGuessBlock
          enableDeepAnalysis={stepNumber === 1}
          onClickDeepAnalysis={onClickDeepAnalysis}
        /> }
      </div>;
    } else if (stepNumber === 3) {
      const enrichedBodyLines = AiIssueAnalysisUtil.enrichQuotedLogLines(bodyLines, (line) => {
        return quotedLogs?.findIndex(({ log }) => log && line.includes(log)) ?? -1;
      });
      // 用两个换行连接，不然markdown不认为是换行
      let allLinesText = enrichedBodyLines.join('\n\n');

      customElementBlock = <ReactMarkdown
        remarkPlugins={[gfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        linkTarget='_blank'
        children={allLinesText}
        components={contextualMarkdownComponents}
      />;
    } else if (stepNumber !== 4) {
      markdownText = bodyLines.join('\n');
    } else {
      const rootCauseIndex = bodyLines.findIndex(line => line.includes('最可能根因'));
      if (rootCauseIndex !== -1 && rootCauseIndex + 1 < bodyLines.length) {
        rootCauseText = bodyLines[rootCauseIndex + 1];
        const updatedBodyLines = [...bodyLines];
        updatedBodyLines.splice(rootCauseIndex, 2);
        markdownText = updatedBodyLines.join('\n');
      } else {
        markdownText = bodyLines.join('\n');
      }
    }

    return {
      rootCauseText,
      markdownText,
      customElementBlock,
    };
  }, [stepNumber, bodyLines, quotedLogs, enableDeepAnalysis, onClickDeepAnalysis, contextualMarkdownComponents, markdownComponents]);

  return (
    <div style={{ display: 'flex' }}>
      {/* 左侧序号圆球和时间线 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '12px'
      }}>
        {/* 序号圆球 */}
        { stepIcon || <div style={{
          width: '32px',
          height: '32px',
          backgroundColor: CS_STYLES.getPrimaryColor(),
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 'bold',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {stepNumber || '•'}
        </div> }

        {(
          <div style={{
            width: '2px',
            backgroundColor: '#d9d9d9',
            flexGrow: 1,
            minHeight: '20px'
          }} />
        )}
      </div>

      {/* 右侧内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginTop: '3px',
          marginBottom: '12px',
          color: '#333',
          lineHeight: '1.4'
        }}>
          {title}
        </div>

        { customElementBlock }

        {/* 最可能根因 */}
        { rootCauseText && <div
          style={{
            backgroundColor: '#ffd8d2',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '8px',
          }}
        >{ze('最可能根因：', 'Most Likely Root Cause: ')}{ rootCauseText }</div>}

        {/* Markdown内容 */}
        <div style={{
          paddingLeft: '0px',
          paddingBottom: '8px',
          lineHeight: '1.6',
        }}>
                  <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={markdownText}
          components={contextualMarkdownComponents}
        />
        </div>
      </div>
    </div>
  );
};

const AiIssueAnalysisAnswerRenderer = ({
  text,
  renderRawText,
  otherInfo,
  enableDeepAnalysis,
  onClickDeepAnalysis = () => {},
}) => {
  const onClickQuotedLog = AiDrawerHooks.useIssueAnalysisQuoteHandler();

  /**
   * 判断输入数据是否为 JSON 格式
   * 
   * 判断逻辑：
   * 1. 去除可能存在的 ```json 和 ``` 标记
   * 2. 检查是否以 { 开头
   * 3. 尝试 JSON.parse，成功则认为是 JSON 格式
   * 4. 如果不是以 { 开头，检查是否包含 "step1InitialHypotheses" 关键字（混合格式）
   * 
   * 返回值：
   * - isJson: 是否为 JSON 格式
   * - jsonData: 解析后的 JSON 数据
   * - prefixText: JSON 内容前的文本（混合格式时存在）
   * - suffixText: JSON 内容后的文本（混合格式时存在）
   */
  const { isJson, jsonData, prefixText, suffixText } = useMemo(() => {
    if (!text) {
      return { isJson: false, jsonData: null, prefixText: '', suffixText: '' };
    }
    
    // 去掉开头的```json和结尾的```
    let processedText = text.trim()
      .replace(/^```json\n/, '')
      .replace(/```$/, '')
      .trimStart();
    
    // 判断是否以左花括号开头
    const isJsonFormat = processedText.startsWith('{');
    
    if (isJsonFormat) {
      try {
        const parsed = JSON.parse(processedText);
        return { isJson: true, jsonData: parsed, prefixText: '', suffixText: '' };
      } catch (e) {
        // JSON 解析失败，仍当作非 JSON 处理
        return { isJson: false, jsonData: null, prefixText: '', suffixText: '' };
      }
    }
    
    // 如果不是以 { 开头，检查是否包含 "step1InitialHypotheses" 关键字（混合格式）
    // --story=131369348 【CS】【NGR】离线分析适配后台生成的不规范的json数据
    if (text.includes('step1InitialHypotheses')) {
      const trimmedText = text.trim();
      const jsonStartMatch = trimmedText.match(/```json\s*/);
      
      if (jsonStartMatch) {
        const jsonStartIndex = jsonStartMatch.index + jsonStartMatch[0].length;
        const beforeJson = trimmedText.substring(0, jsonStartMatch.index);
        const afterJsonStart = trimmedText.substring(jsonStartIndex);
        
        // 查找最后一个 ```
        const lastBacktickIndex = afterJsonStart.lastIndexOf('```');
        let jsonContent;
        let afterJson = '';
        
        if (lastBacktickIndex !== -1) {
          // 找到了结尾的 ```
          jsonContent = afterJsonStart.substring(0, lastBacktickIndex);
          afterJson = afterJsonStart.substring(lastBacktickIndex + 3).trim();
        } else {
          // 没找到结尾的 ```，从 ```json 到末尾都视作 JSON 内容
          jsonContent = afterJsonStart;
        }
        
        try {
          const parsed = JSON.parse(jsonContent.trim());
          return {
            isJson: true,
            jsonData: parsed,
            prefixText: beforeJson.trim(),
            suffixText: afterJson,
          };
        } catch (e) {
          // JSON 解析失败，仍当作非 JSON 处理
          return { isJson: false, jsonData: null, prefixText: '', suffixText: '' };
        }
      }
    }
    
    return { isJson: false, jsonData: null, prefixText: '', suffixText: '' };
  }, [text]);

  // console.log('gomox1', text);
  const convertedText = AiDrawerHooks.useConvertAnswerToMarkdownText(text);
  // console.log('gomox2', convertedText);

  const lines = useMemo(() => {
    return (convertedText || '').split('\n');
  }, [convertedText]);

  const sections = useMemo(() => {
    return makeSectionsFromLinesByHeaderRegex(lines, /^(?:[#*]*\s*)(?:Step|修复建议|Fix Recommendations)/);
  }, [lines]);

  const quotedLogs = useMemo(() => {
    return otherInfo?.quotedLogs || [];
  }, [otherInfo]);

  // 创建包含 quotedLogs 上下文的 markdownComponents
  const contextualMarkdownComponents = useMemo(() => {
    return {
      ...markdownComponents,
      'quoted-log-btn': ({ node, ...props }) => {
        const quotedLogIndex = Number(props.children);

        return (
          <div style={{ display: 'inline-block'}}>
            <Tooltip
              title={ze('链接到日志位置', 'Link to log position')}
            ><CsSimpleButton
              style={{ marginLeft: '4px' }}
              className={scss.quoteLogButton}
              onClick={() => {
                const quotedLogInfo = quotedLogs[quotedLogIndex];
                if (quotedLogInfo) {
                  const { log, logCategory, crashIdOfLog, matchedLogLine } = quotedLogInfo;
                  onClickQuotedLog(crashIdOfLog, logCategory, log, matchedLogLine);
                }
              }}
            >
              <LinkOutlined />
            </CsSimpleButton></Tooltip>
          </div>
        );
      },
    };
  }, [quotedLogs, onClickQuotedLog]);

  // 如果需要渲染原始文本（调试模式）
  if (renderRawText) {
    return <div style={{ whiteSpace: 'pre-wrap' }}>{ text }</div>;
  }

  /**
   * 根据数据格式选择渲染器：
   * - JSON 格式：使用新的 AiIssueAnalysisJsonAnswerRenderer 直接渲染
   * - 如果存在 prefixText 或 suffixText，则在 JSON 渲染器前后各加一个 ReactMarkdown
   * - Markdown 格式：使用旧的 Markdown 渲染逻辑（通过 AnswerSectionRenderer）
   */
  if (isJson && jsonData) {
    return <>
      {prefixText && (
        <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={prefixText}
          components={markdownComponents}
        />
      )}
      <AiIssueAnalysisJsonAnswerRenderer
        jsonData={jsonData}
        otherInfo={otherInfo}
        enableDeepAnalysis={enableDeepAnalysis}
        onClickDeepAnalysis={onClickDeepAnalysis}
      />
      {suffixText && (
        <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={suffixText}
          components={markdownComponents}
        />
      )}
    </>;
  }

  // 否则使用 Markdown 渲染器（兼容旧格式）
  return <ErrorBoundary fallback={<div>
    <ReactMarkdown
      remarkPlugins={[gfm]}
      linkTarget='_blank'
      children={text}
      components={markdownComponents}
    />
  </div>}>{
    sections.map((x, i) => {
      return <AnswerSectionRenderer
        key={i}
        section={x}
        otherInfo={otherInfo}
        enableDeepAnalysis={enableDeepAnalysis}
        onClickDeepAnalysis={onClickDeepAnalysis}
        contextualMarkdownComponents={contextualMarkdownComponents}
      />;
    })
  }</ErrorBoundary>
};

export default AiIssueAnalysisAnswerRenderer;
